import type { ComponentProps } from 'react';
import { Badge, cx } from '@/ui/primitives';
import { KIND_LABEL, KIND_TONE, type Release, type ReleaseEntry } from './api';

const MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/** `2026-08-12` → `12 Ağustos 2026`. The date is a fact about the release, not a timestamp. */
export function releaseDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/**
 * The order the reader wants, which is not the order the git log gives.
 *
 * What is NEW first, what was FIXED after it: someone opening this screen is
 * asking "what can I do now that I could not yesterday" before "what stopped
 * being broken". Within a kind the history order is kept.
 */
const KIND_ORDER: Record<ReleaseEntry['kind'], number> = { yeni: 0, duzeltildi: 1, hizlandi: 2 };

export function sortEntries(entries: ReleaseEntry[]): ReleaseEntry[] {
  return [...entries].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
}

/**
 * One change: kind, area, sentence — three columns wide, a stack narrow.
 *
 * The two leading columns are fixed widths rather than content-derived, so the
 * badges of every row in every release sit on one vertical edge and the
 * sentences start on a second. A reader scanning a year of releases for "what
 * broke in Ödeme" follows two straight lines instead of a ragged one; the old
 * `flex` row re-measured itself per entry, which is what made the text look
 * centred when the badge and the area happened to be short.
 *
 * The breakpoint is a CONTAINER query, not `sm:`. The same row renders inside a
 * ~460px dialog and on a ~1100px page, and a viewport query cannot tell those
 * apart — it would hand the dialog a three-column grid it has no room for.
 */
function EntryRow({
  tone,
  label,
  area,
  text,
  breaking,
  muted,
}: {
  tone: ComponentProps<typeof Badge>['tone'];
  label: string;
  area?: string | null;
  text: string;
  breaking?: boolean;
  muted?: boolean;
}) {
  return (
    // Narrow: kind and area share a line, the sentence takes the next one — two
    // lines per change rather than three, which is what keeps a phone screenful
    // at four or five releases instead of two.
    <li className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0 @2xl:grid-cols-[6rem_8.5rem_minmax(0,1fr)] @2xl:gap-x-4">
      <span className="justify-self-start">
        <Badge tone={tone}>{label}</Badge>
      </span>
      {/* Kept in the flow even when empty, or the sentence would slide into the
          area column and break the alignment the fixed widths exist for. Narrow
          there is no column to hold, so an absent area collapses instead of
          spending a row gap on nothing. */}
      <span
        className="text-fg-muted min-w-0 text-[13px] leading-[1.5] font-medium empty:hidden @2xl:truncate @2xl:empty:block"
        title={area ?? undefined}
      >
        {area}
      </span>
      <span
        className={cx(
          'col-span-2 min-w-0 text-left text-sm leading-[1.5] break-words @2xl:col-span-1',
          muted ? 'text-fg-muted' : 'text-fg',
        )}
      >
        {text}
        {breaking && <span className="text-danger ml-1.5 text-[13px] font-semibold">· dikkat</span>}
      </span>
    </li>
  );
}

/**
 * One release, rendered the same way in the dialog and on the page.
 *
 * Shared rather than written twice, because the two surfaces answer the same
 * question at different moments — "what changed just now" and "what changed
 * ever" — and two copies would drift into two different answers.
 *
 * A release with nothing user-visible in it is a row like any other, carrying a
 * neutral "Altyapı" badge. It used to be a bare paragraph, which made the one
 * release in ten that has no features look like a rendering fault rather than
 * an ordinary week.
 */
export function ReleaseBody({ release }: { release: Release }) {
  const entries = sortEntries(release.entries);

  return (
    <ul className="divide-border @container divide-y">
      {entries.length === 0 ? (
        <EntryRow
          tone="neutral"
          label="Altyapı"
          text="Bu sürümde kullanıcıya görünen bir değişiklik yok — yalnızca altyapı işleri."
          muted
        />
      ) : (
        entries.map((entry, i) => (
          <EntryRow
            key={`${release.version}-${i}`}
            tone={KIND_TONE[entry.kind]}
            label={KIND_LABEL[entry.kind]}
            area={entry.area}
            text={entry.text}
            breaking={entry.breaking}
          />
        ))
      )}
    </ul>
  );
}
